// =============================================================================
// VINUM - Cart API
// Aligned with backend /order-cart-items endpoints
// Transforms backend OrderCartItem → frontend CartItem
// =============================================================================

import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { CartItem, Cart } from '../types';

// -----------------------------------------------------------------------------
// Transform: backend OrderCartItem → frontend CartItem
// Backend returns: { id, orderId, itemId, userId, unitPrice, quantity, lineTotal,
//                    item: { id, itemCode, itemDescription, ..., image?: { id } } }
// Frontend expects: { id, userId, itemId, name, unitPrice, quantity, totalPrice, imageUrl }
// -----------------------------------------------------------------------------

function buildCartImageUrl(imageId?: string | null): string | undefined {
  if (!imageId) return undefined;
  return `/media/file/${imageId}/medium`;
}

function transformBackendCartItem(raw: any): CartItem {
  if (!raw) return raw;

  const unitPrice = Number(raw.unitPrice) || 0;
  const quantity = Number(raw.quantity) || 0;
  const lineTotal = Number(raw.lineTotal) || unitPrice * quantity;

  // Extract image ID from nested item.image relation
  const imageId = raw.item?.image?.id ?? null;

  return {
    id: raw.id,
    userId: raw.userId,
    itemId: raw.itemId,
    name: raw.item?.itemDescription || raw.name || 'Articolo',
    unitPrice,
    quantity,
    totalPrice: lineTotal,
    imageUrl: buildCartImageUrl(imageId),
    synced: true,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

// -----------------------------------------------------------------------------
// Cart API Endpoints
// Backend uses /order-cart-items/open/cart for cart operations
// -----------------------------------------------------------------------------

export const CartAPI = {
  /**
   * Get cart items for current user
   * GET /order-cart-items/open/cart
   * Backend returns OrderCartItem[] (array) with relations ['item', 'item.image']
   */
  async getCart(): Promise<Cart> {
    const response = await apiGet<any>('/order-cart-items/open/cart');

    // Backend always returns an array of OrderCartItem entities
    const rawItems = Array.isArray(response) ? response : response.items || [];
    const items: CartItem[] = rawItems.map(transformBackendCartItem).filter(Boolean);

    const totalItems = items.reduce((sum: number, item: CartItem) => sum + item.quantity, 0);
    const totalPrice = items.reduce((sum: number, item: CartItem) => sum + item.totalPrice, 0);

    return { items, totalItems, totalPrice };
  },

  /**
   * Get all order cart items (admin)
   * GET /order-cart-items
   */
  async getAllCartItems(): Promise<CartItem[]> {
    const response = await apiGet<any[]>('/order-cart-items');
    return (response || []).map(transformBackendCartItem).filter(Boolean);
  },

  /**
   * Add item to cart (delta quantity — backend increments existing)
   * POST /order-cart-items/open/cart
   * Backend expects: { itemId, unitPrice, quantity (delta) }
   * Returns: OrderCartItem with item relation
   */
  async addItem(params: {
    itemId: string;
    unitPrice: number;
    quantity?: number;
  }): Promise<CartItem> {
    const response = await apiPost<any>('/order-cart-items/open/cart', {
      itemId: params.itemId,
      unitPrice: params.unitPrice,
      quantity: params.quantity ?? 1,
    });

    // Handle "removed" response when deltaQty results in qty <= 0
    if (response?.removed) {
      return {
        itemId: params.itemId,
        userId: '',
        name: '',
        unitPrice: 0,
        quantity: 0,
        totalPrice: 0,
      };
    }

    return transformBackendCartItem(response);
  },

  /**
   * Set absolute quantity for a cart item
   * PUT /order-cart-items/open/cart/:itemId
   * Backend expects: { quantity, unitPrice? }
   */
  async setQuantity(itemId: string, quantity: number, unitPrice?: number): Promise<CartItem | null> {
    const body: any = { quantity };
    if (unitPrice != null) body.unitPrice = unitPrice;

    const response = await apiPut<any>(`/order-cart-items/open/cart/${itemId}`, body);

    // Handle "removed" response when quantity <= 0
    if (response?.removed) return null;

    return transformBackendCartItem(response);
  },

  /**
   * Remove item from cart
   * DELETE /order-cart-items/open/cart/:itemId
   */
  async removeItem(itemId: string): Promise<void> {
    await apiDelete(`/order-cart-items/open/cart/${itemId}`);
  },

  /**
   * Attach open cart to order (checkout)
   * POST /order-cart-items/open/cart/attach
   */
  async attachCartToOrder(orderId: string): Promise<void> {
    await apiPost('/order-cart-items/open/cart/attach', { orderId });
  },

  /**
   * Clear all cart items for current user
   * Fetches current cart and removes each item
   */
  async clearCart(): Promise<void> {
    const cart = await this.getCart();
    await Promise.all(cart.items.map(item => this.removeItem(item.itemId)));
  },

  /**
   * Get cart count
   */
  async getCartCount(): Promise<number> {
    const cart = await this.getCart();
    return cart.totalItems;
  },
};
